import { useEffect, useRef, type Dispatch, type SetStateAction } from "react";
import type {
  GameBookImportRequest,
  GameBookImportReceipt,
} from "../../shared/contracts/gamebook-import.js";
import {
  GameBookImportRejected,
  postGameBookImport,
} from "../pages/tee-off/ggb/ggb-import-api.js";
import { roundJournal } from "../data/local-database.js";
import { SeasonRoundStartStore } from "../sync/season-round-start-store.js";
import { executeServerImport } from "../pages/tee-off/ggb/ggb-server-executor.js";
import { importValue } from "../pages/tee-off/ggb/ggb-import-value.js";
import type { ActionContext } from "./round-workspace-sync.js";

export interface ServerImportWorkspaceActions {
  importGameBookRound(
    input: GameBookImportRequest,
  ): Promise<GameBookImportReceipt | null>;
}
export function useServerImportWorkspace(
  context: ActionContext,
  setBusy: Dispatch<SetStateAction<boolean>>,
): ServerImportWorkspaceActions {
  const latest = useRef(context);
  latest.current = context;
  const pending = useRef<{
    key: string;
    accountId: string;
    job: Promise<GameBookImportReceipt | null>;
  } | null>(null);
  useEffect(() => {
    pending.current = null;
  }, [context.accountId]);
  return {
    importGameBookRound: (input) => {
      const key = importValue(input),
        active = pending.current;
      if (active)
        return active.key === key && active.accountId === context.accountId
          ? active.job
          : Promise.resolve(null);
      const job = runServerImport(
        context,
        () => latest.current,
        setBusy,
        structuredClone(input),
      );
      pending.current = { key, accountId: context.accountId, job };
      const clear = () => {
        if (pending.current?.job === job) pending.current = null;
      };
      void job.then(clear, clear);
      return job;
    },
  };
}
async function runServerImport(
  context: ActionContext,
  current: () => ActionContext,
  setBusy: Dispatch<SetStateAction<boolean>>,
  request: GameBookImportRequest,
): Promise<GameBookImportReceipt | null> {
  const access = () => {
    const next = current();
    if (
      next.accountId !== context.accountId ||
      !next.authenticated ||
      !next.online
    )
      throw new Error(
        "Connect and sign in to the original account to continue this import.",
      );
    if (new SeasonRoundStartStore(localStorage).pending(context.accountId))
      throw new Error(
        "Continue or abandon your season setup before importing a historical round.",
      );
    if (next.conflicts.length)
      throw new Error("Resolve the open score conflict before importing.");
  };
  setBusy(true);
  try {
    access();
    await context.autoSync.settle();
    const receipt = await executeServerImport({
      accountId: context.accountId,
      request,
      journal: roundJournal,
      access,
      post: postGameBookImport,
    });
    access();
    context.setSnapshot(receipt.snapshot);
    context.setQueued(false);
    context.setMessage(
      receipt.duplicate
        ? "This round was already saved. Opened its existing result."
        : receipt.snapshot.state === "active"
          ? "Imported scores ready to review."
          : "Imported round confirmed on the server.",
    );
    return receipt;
  } catch (error) {
    if (current().accountId === context.accountId)
      context.setMessage(
        error instanceof Error ? error.message : "The import could not finish.",
      );
    if (error instanceof GameBookImportRejected) throw error;
    return null;
  } finally {
    if (current().accountId === context.accountId) setBusy(false);
  }
}
